import {Trans} from '@lingui/macro';
import _, {noop} from 'lodash';
import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {ScrollDirectionsEnum} from '../../../srcCommon/scroll/scrollerTypes';
import {useLinkNavigation} from '../../components/common/navigation/useLinkNavigation';
import {IconCell} from '../../components/common/table/cells/iconCell';
import {VirtualTableColumnCellsEnum} from '../../components/common/table/helpers/tableHelpers';
import {StaticVirtualTable} from '../../components/common/table/staticVirtualTable';
import {TableColumn} from '../../components/common/table/tableColumn';
import {TableRow} from '../../components/common/table/tableRow';
import {VirtualTableRowRenderer} from '../../components/common/table/virtualTableBase';
import {buildAdvancedNotificationRuleDescription} from '../../helpers/models/notificationRulesHelpers';
import {useAppSelector} from '../../helpers/reduxHelpers';
import {buildSettingsPath} from '../../models/url/settingsUrlHelpers';
import {SettingsNotificationsUrl, SettingsResourcePagesEnum} from '../../models/url/settingsUrlModel';
import {AsyncMoreStatusesEnum} from '../../store/appTypes';
import {getInboxes} from '../../store/data/inboxes/inboxesSelectors';
import {getUserNotificationRules} from '../../store/data/teammates/teammatesSelectors';

/*
 * Props.
 */

interface SettingsAdvancedNotificationsTableProps {
  url: SettingsNotificationsUrl;
}

/*
 * Style.
 */

const StyledEmptyStateContainerDiv = styled.div`
  position: absolute;
  top: 80px;
  left: 0;
  right: 0;
  color: ${(p) => p.theme.greys.shade70};
  text-align: center;
`;

const StyledTableRow = styled(TableRow)`
  min-height: 56px;
  background: ${(p) => p.theme.greys.white};
`;

const StyledBoldSpan = styled.span`
  font-weight: ${fontWeights.bold};
`;

/*
 * Component.
 */

enum AdvancedNotificationsTableColumnsEnum {
  NOTIFICATION_RULES = 'Notification rules',
}

const advancedNotificationsTableColumns: ReadonlyArray<TableColumn<AdvancedNotificationsTableColumnsEnum>> = [
  {
    id: AdvancedNotificationsTableColumnsEnum.NOTIFICATION_RULES,
    title: <Trans id="settingsnotifications-notificationsrules">Notification rules</Trans>,
    type: AdvancedNotificationsTableColumnsEnum.NOTIFICATION_RULES,
    cellType: VirtualTableColumnCellsEnum.ADVANCED_NOTIFICATION_RULE,
    settings: {
      cellWidth: {
        shouldFillRemainingWidth: true,
      },
    },
  },
];

export const SettingsAdvancedNotificationsTable: FC<SettingsAdvancedNotificationsTableProps> = (props) => {
  const notificationRules = useAppSelector(getUserNotificationRules);

  const advancedRulesMap = useMemo(
    () =>
      _(notificationRules.advanced)
        .map((r, index) => ({id: index + 1, rule: r}))
        .keyBy((r) => r.id)
        .mapValues((r) => ({...r.rule, id: r.id}))
        .value(),
    [notificationRules.advanced],
  );
  const ruleIds = useMemo(
    () =>
      _(advancedRulesMap)
        .values()
        .map((r) => r.id)
        .value(),
    [advancedRulesMap],
  );
  const {navigateToLink} = useLinkNavigation();
  const inboxes = useAppSelector(getInboxes);

  const renderEmptyState = () => (
    <StyledEmptyStateContainerDiv>
      <Trans id="notifications-table-empty-state">You do not have any notification rules</Trans>
    </StyledEmptyStateContainerDiv>
  );
  const theme = useUserTheme();

  const renderRow: VirtualTableRowRenderer<number> = (ruleId, options) => {
    const rule = advancedRulesMap[ruleId];
    if (!rule) {
      return null;
    }

    return (
      <StyledTableRow
        hasHover
        onSelect={() => {
          navigateToLink(
            buildSettingsPath({
              ...props.url,
              notificationIndex: ruleId - 1,
              resourcePage: SettingsResourcePagesEnum.EDIT,
            }),
          );
        }}
        renderColumn={(isHovering) => (
          <IconCell
            index={0}
            key={ruleId}
            value={buildAdvancedNotificationRuleDescription(rule, inboxes, StyledBoldSpan)}
            useNormalFontWeight
            settings={advancedNotificationsTableColumns[0].settings}
            isHovering={isHovering}
            iconSize={VisualSizesEnum.MEDIUM}
            iconName="bell"
            size={options.size}
            backgroundColor={theme.greys.shade30}
          />
        )}
      />
    );
  };

  return (
    <StaticVirtualTable<number>
      renderRow={renderRow}
      columns={advancedNotificationsTableColumns}
      hasData={false}
      items={ruleIds}
      shouldRenderEmptyState={ruleIds.length === 0}
      onLoadMore={noop}
      renderEmpty={renderEmptyState}
      moreStatuses={{
        [ScrollDirectionsEnum.TOP]: AsyncMoreStatusesEnum.ALL_LOADED,
        [ScrollDirectionsEnum.BOTTOM]: AsyncMoreStatusesEnum.ALL_LOADED,
      }}
    />
  );
};
